/* ============================================================
   Today — the mobile screen's shape, with room, under the sky: the greeting
   and the big date on the horizon with the sun at the height of the day's
   progress, the week strip in a frosted pill, the capture bar straddling the
   horizon, the one pinned card, the habit tile grid, the day's tasks in a
   card, then what's resolved. Any day can be viewed (`?d=`); Today is the
   default and the only day that celebrates.
   ============================================================ */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { addDaysStr, type Habit, type Task, toDayStr, todayStr, useStore, useWeekStart, weekStartOf } from '@habishine/client-core';

import { Capture, CAPTURE_INPUT_ID } from '../components/Capture';
import { Confetti } from '../components/Confetti';
import { Icon } from '../components/Icon';
import { Ring } from '../components/Ring';
import { Sky } from '../components/Sky';
import { CardGroup, DoneRow, GhostAdd, HabitTile, SectionHead, SkippedRow, TaskRow, Toggle } from '../components/rows';
import { SortableList } from '../components/Sortable';
import { Page } from '../components/ui';
import { DayHeader, dayTitle, greeting, WeekStrip } from '../components/WeekStrip';
import { useEditors } from '../shell/Editors';
import { PhoneNudge } from '../shell/MobileNav';
import { shineLine, useToast } from '../shell/Toast';
import { useNarrow, usePhone } from '../shell/useNarrow';
import { hueVar } from '../theme/hues';

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function TodayPage() {
  const store = useStore();
  const { openTask, openHabit } = useEditors();
  const { toast } = useToast();
  const { weekStartsOn } = useWeekStart();
  const [params, setParams] = useSearchParams();
  const narrow = useNarrow();
  const phone = usePhone();
  const today = todayStr();
  const day = DAY_RE.test(params.get('d') ?? '') ? params.get('d')! : today;
  const isToday = day === today;
  const setDay = (d: string) => setParams(d === today ? {} : { d });
  // a preview knob for the sky: `?hour=7.5` pins the clock (design review, screenshots)
  const hourParam = Number(params.get('hour'));
  const skyHour = Number.isFinite(hourParam) && params.has('hour') ? hourParam : undefined;

  const habits = store.habitsForDay(day).filter((h) => h.dueToday);
  const tasks = store.tasksForDay(day);
  const done = store.doneForDay(day);
  const skipped = store.skippedForDay(day);
  const pin = store.pinnedItem(day);
  const { eyebrow, title } = dayTitle(day);

  const weekStart = toDayStr(weekStartOf(new Date(day.replace(/-/g, '/')), weekStartsOn));
  const dayCounts = useMemo(() => store.dayActivityCounts(addDaysStr(weekStart, -7), addDaysStr(weekStart, 20)), [store, weekStart]);

  const allDone = tasks.length > 0 || habits.length > 0 || done.length > 0 ? tasks.length === 0 && habits.every((h) => h.done) && (done.length > 0 || habits.length > 0) : false;
  const [fire, setFire] = useState(0);
  const wasDone = useRef(allDone);
  useEffect(() => {
    if (isToday && allDone && !wasDone.current) setFire((n) => n + 1);
    wasDone.current = allDone;
  }, [allDone, isToday]);

  // the day at a glance: every due habit and every task on the day, done or not
  const total = habits.length + tasks.length + done.length;
  const finished = habits.filter((h) => h.done).length + done.length;
  const left = total - finished;
  const subline = total === 0 ? null : left === 0 ? 'Everything done' : `${left} to go · ${finished} done`;
  // the sun's height: a day with nothing on it sits low and calm, never "full"
  const progress = total === 0 ? 0.08 : finished / total;

  // Celebrate the REP (Fogg's "Shine"), not the day: the moment a habit
  // completes — the only tap for a daily one, the last of a count for a
  // multi — a line lands. Undoing says nothing; the store's own `celebrated`
  // is reserved for the whole day, which the confetti handles.
  const checkIn = (h: Habit) => {
    const finishing = !h.done && (h.target <= 1 || h.progress + 1 >= h.target);
    store.completeItem(h.id, day);
    if (finishing) toast(shineLine(h.name.length + finished), { emoji: h.emoji || '✦' });
  };

  // Done and Skipped start expanded: a finished row is the day's evidence of
  // progress, so it stays in view (Things 3 shows logged items inline) — the
  // toggle is there for the person who wants a shorter page, not the default.
  const [doneOpen, setDoneOpen] = useState(true);
  const [skippedOpen, setSkippedOpen] = useState(true);
  // the seven days ending on the one on screen, for each tile's dots
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDaysStr(day, i - 6)), [day]);
  const tomorrow = store.tasksForDay(addDaysStr(day, 1));

  const habitList = habits.filter((h) => h.id !== pin?.item.id);
  const taskList = tasks.filter((t) => t.id !== pin?.item.id);
  const pinTask = pin?.project === 'tasks' ? (pin.item as Task) : null;
  const pinHabit = pin?.project === 'habits' ? (pin.item as Habit) : null;
  const pinPct = pinHabit ? (pinHabit.target > 1 ? pinHabit.progress / pinHabit.target : pinHabit.done ? 1 : 0) : pinTask?.done ? 1 : 0;
  const pinMeta = pinHabit
    ? pinHabit.done ? 'Done today' : pinHabit.target > 1 ? `${pinHabit.progress} of ${pinHabit.target} today` : 'Click the ring to check in'
    : pinTask ? (store.projectForTask(pinTask)?.name ?? (pinTask.remindAt != null ? 'Timed' : 'Click the ring to mark done')) : '';
  const pinHue = pinTask ? store.hueForTask(pinTask) : pin?.item.hue ?? 'purple';

  return (
    <div className="screen-in">
      <Confetti fireKey={fire} />

      {/* the sky, with the header on it */}
      {/* keyed on the day so switching days REPOSITIONS the sun rather than
         animating it — a glide reads as time passing, and only the sky's
         colour follows the clock; the sun follows the day on screen */}
      <Sky key={day} progress={progress} height={narrow ? 216 : 300} hour={skyHour} sunTitle={isToday ? 'The sun rises as today gets done' : `How much of this day got done`}>
        <Page className="flex h-full flex-col justify-end pb-4 md:pb-7">
          <div className="mb-1.5 text-[13px] font-semibold text-[var(--sky-ink-2)] md:mb-2 md:text-[13.5px]">{isToday ? greeting(skyHour != null ? Math.floor(skyHour) : undefined) : 'Another day'}</div>
          <DayHeader
            onSky
            eyebrow={eyebrow}
            title={title}
            offToday={!isToday}
            onToday={() => setDay(today)}
            right={
              subline ? (
                <span className="glass flex items-center gap-2 rounded-full py-1.5 pl-2 pr-3 text-[12.5px] font-semibold text-[var(--sky-ink)] md:pr-3.5 md:text-[13px]" title={`${finished} of ${total} done`}>
                  <Ring size={22} stroke={3.5} hue="yellow" pct={total ? finished / total : 0} check={left === 0} track="sunk" />
                  <span className="hidden sm:inline">{subline}</span>
                  <span className="num sm:hidden">{finished}/{total}</span>
                </span>
              ) : null
            }
          />
          <WeekStrip onSky selected={day} onSelect={setDay} dayCounts={dayCounts} weekStartsOn={weekStartsOn} />
        </Page>
      </Sky>

      <Page>
        <Capture className="mt-4 md:mt-6" defaultToday day={day} placeholder={isToday ? 'Add a task for today — #project @tag, "5pm", "every monday"' : `Add a task for ${title.toLowerCase()}`} />

        {phone && isToday ? <PhoneNudge /> : null}

        {/* the one pin */}
        {pin ? (
          <div className="rise-in surface relative mt-4 flex items-center gap-3 overflow-hidden py-3.5 pl-5 pr-3 md:mt-5 md:gap-4 md:py-4 md:pl-6 md:pr-4" style={{ ['--i' as string]: 1 }}>
            <span className="absolute bottom-0 left-0 top-0 w-[6px]" style={{ background: `linear-gradient(180deg, ${hueVar(pinHue, 'c')}, var(--frog))` }} />
            <span className="pointer-events-none absolute -left-10 -top-10 h-40 w-40 rounded-full opacity-60" style={{ background: `radial-gradient(closest-side, ${hueVar(pinHue, 't')}, transparent)` }} aria-hidden />
            <span className="relative text-2xl">{pinHabit ? pinHabit.emoji || '✦' : <Icon name="pin" size={22} color="var(--frog)" strokeWidth={2.2} filled />}</span>
            <button type="button" className="relative min-w-0 flex-1 text-left" onClick={() => (pinHabit ? openHabit(pinHabit.id, day) : pinTask ? openTask(pinTask.id, day) : undefined)}>
              <div className="text-[11px] font-bold uppercase tracking-[1.2px] text-frog">Pinned · do this first</div>
              <div className="font-display truncate text-[20px] font-semibold leading-[1.15] text-ink md:text-[24px]">{pin.item.name}</div>
              <div className="text-[13px] text-meta">{pinMeta}</div>
            </button>
            <button type="button" aria-label={`Complete ${pin.item.name}`} className="ringbtn relative rounded-full" style={{ ['--ring-glow' as string]: `color-mix(in srgb, ${hueVar(pinHue, 'c')} 55%, transparent)` }} onClick={() => (pinHabit ? checkIn(pinHabit) : store.completeItem(pin.item.id, day))}>
              <Ring size={40} stroke={4.5} hue={pinHue} pct={pinPct} check />
            </button>
            <button type="button" aria-label="Unpin" title="Unpin" onClick={() => store.togglePin(pin.item.id, day)} className="relative rounded-full p-1.5 text-tertiary transition hover:bg-card-sunk hover:text-ink">
              <Icon name="x" size={14} />
            </button>
          </div>
        ) : (
          <div className="rise-in mt-4 flex items-center gap-3 rounded-card border-[1.5px] border-dashed border-hairline px-4 py-3.5 text-[13.5px] font-medium text-meta md:mt-5 md:px-5 md:text-[14px]" style={{ ['--i' as string]: 1 }}>
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-frog-soft text-frog"><Icon name="pin" size={14} /></span>
            Pin your one important thing — open any row and choose Pin.
          </div>
        )}

        {/* wide windows get two columns — habits beside tasks — the way a desktop
           day planner lays a morning out; narrower ones stack, like the phone */}
        <div className={habitList.length > 0 ? 'xl:grid xl:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] xl:gap-8' : ''}>
          <div>
            {habitList.length > 0 ? (
              <>
                <SectionHead count={habitList.length}>Habits</SectionHead>
                <SortableList
                  items={habitList}
                  layout="grid"
                  className="grid grid-cols-2 gap-2.5 md:grid-cols-3 md:gap-3 xl:grid-cols-2"
                  onMove={(from, to) => store.moveHabit(habitList, from, to)}
                  render={(h, i) => <HabitTile habit={h} index={i + 2} pinned={false} onOpen={() => openHabit(h.id, day)} onCheckIn={() => checkIn(h)} weekDays={weekDays} checkins={store.state.checkins[h.id]} />}
                />
              </>
            ) : null}
          </div>
          <div>
            <SectionHead count={taskList.length}>Tasks</SectionHead>
            {taskList.length > 0 ? (
              <CardGroup index={2}>
                <SortableList
                  items={taskList}
                  onMove={(from, to) => store.moveTask(taskList, from, to)}
                  render={(t, i) => (
                    <TaskRow
                      task={t}
                      first={i === 0}
                      index={i + 3}
                      hue={store.hueForTask(t)}
                      projectNote={store.projectForTask(t)?.name}
                      tagNote={store.tagsOfTask(t).map((g) => g.name)}
                      onOpen={() => openTask(t.id, day)}
                      onComplete={() => store.completeItem(t.id)}
                    />
                  )}
                />
              </CardGroup>
            ) : allDone ? (
              /* the day is done: the sun fully up with its rays, a calm line, and a
                 glance at tomorrow — the way Sunsama closes a day, without a score */
              <Sky progress={1} height={176} className="rise-in rounded-card shadow-card" >
                <div className="flex h-full flex-col justify-end p-6">
                  <div className="font-display text-[30px] font-semibold leading-none text-[var(--sky-ink)]">{isToday ? 'Day done.' : 'This day is done.'}</div>
                  <div className="mt-2 max-w-[34ch] text-[14px] leading-[1.45] text-[var(--sky-ink-2)]">
                    {isToday ? 'Everything you set out to do, done. ' : ''}
                    {tomorrow.length > 0 ? `Tomorrow starts with “${tomorrow[0]!.name}”${tomorrow.length > 1 ? ` and ${tomorrow.length - 1} more` : ''}.` : 'Tomorrow is a blank page — plan it when you like.'}
                  </div>
                </div>
              </Sky>
            ) : (
              <GhostAdd label={isToday ? 'Nothing scheduled — add something' : `Nothing on ${title.toLowerCase()} yet — add something`} onClick={() => document.getElementById(CAPTURE_INPUT_ID)?.focus()} />
            )}

            {done.length > 0 ? (
              <>
                <Toggle label={`Done ${isToday ? 'today' : 'this day'} (${done.length})`} open={doneOpen} onToggle={() => setDoneOpen((o) => !o)} />
                {doneOpen ? <CardGroup>{done.map((t, i) => <DoneRow key={t.id} task={t} hue={store.hueForTask(t)} first={i === 0} onOpen={() => openTask(t.id, day)} />)}</CardGroup> : null}
              </>
            ) : null}
            {skipped.length > 0 ? (
              <>
                <Toggle label={`Skipped (${skipped.length})`} open={skippedOpen} onToggle={() => setSkippedOpen((o) => !o)} />
                {skippedOpen ? <CardGroup>{skipped.map((t, i) => <SkippedRow key={t.id} task={t} hue={store.hueForTask(t)} first={i === 0} onOpen={() => openTask(t.id, day)} />)}</CardGroup> : null}
              </>
            ) : null}
          </div>
        </div>
      </Page>
    </div>
  );
}
